'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    [key: string]: any;
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 75,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.webp',
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // More reliable external URL detection
    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');
    const isLocal = imageSrc.startsWith('/') || imageSrc.startsWith('./') || imageSrc.startsWith('data:');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const commonClassName = `${className} ${isLoading ? 'bg-gray-200' : ''} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    // For external URLs or when in doubt, use regular img tag
    if (isExternal && !isLocal) {
        const imgStyle: React.CSSProperties = {};

        if (width) imgStyle.width = width;
        if (height) imgStyle.height = height;

        if (fill) {
            return (
                <div data-component-id="src/components/ui/AppImage.tsx:71:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="71" data-component-end-line="82" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                    <img data-component-id="src/components/ui/AppImage.tsx:72:20" data-component-path="src/components/ui/AppImage.tsx" data-component-line="72" data-component-end-line="81" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AimageSrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                        src={imageSrc}
                        alt={alt}
                        className={`${commonClassName} absolute inset-0 w-full h-full object-cover`}
                        onError={handleError}
                        onLoad={handleLoad}
                        onClick={onClick}
                        style={imgStyle}
                        {...props}
                    />
                </div>
            );
        }

        return (
            <img data-component-id="src/components/ui/AppImage.tsx:87:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="87" data-component-end-line="96" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3AimageSrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22%5Bvar%3AcommonClassName%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                src={imageSrc}
                alt={alt}
                className={commonClassName}
                onError={handleError}
                onLoad={handleLoad}
                onClick={onClick}
                style={imgStyle}
                {...props}
            />
        );
    }

    // For local images and data URLs, use Next.js Image component
    const imageProps = {
        src: imageSrc,
        alt,
        className: commonClassName,
        priority,
        quality,
        placeholder,
        blurDataURL,
        unoptimized: true,
        onError: handleError,
        onLoad: handleLoad,
        onClick,
        ...props,
    };

    if (fill) {
        return (
            <div data-component-id="src/components/ui/AppImage.tsx:118:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="118" data-component-end-line="125" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
                <Image data-component-id="src/components/ui/AppImage.tsx:119:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="119" data-component-end-line="124" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    {...imageProps}
                    fill
                    sizes={sizes || '100vw'}
                    style={{ objectFit: 'cover' }}
                />
            </div>
        );
    }

    return (
        <Image data-component-id="src/components/ui/AppImage.tsx:130:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="130" data-component-end-line="134" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            {...imageProps}
            width={width || 400}
            height={height || 300}
        />
    );
}

export default AppImage;